import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'

import { useSnackbar } from '@/hooks/useSnackbar'
import {
  useLazyPayDataBankAccountLookupQuery,
  useLazyPayDataBankLookupQuery,
  useUpdatePayDataMutation,
} from '@/store/api/procurementApi'
import type { PayDataListItem, PayDataLookupItem } from '@/types/api'
import { formatDate } from '@/utils/date'
import { getErrorMessage } from '@/utils/error'

import {
  compactDialogContentSx,
  dialogHeaderBtnSx,
  dialogTitleActionsSx,
  dialogTitleSx,
  dialogTitleTextSx,
  roSx,
  sectionHeaderSx,
} from '../purchase/dialogStyle'

interface Props {
  open: boolean
  cpCode: string
  supplierLabel: string
  /** The ticked Pay Data rows being re-banked (Access subform `frmProcurementQryPayDataBank`). */
  rows: PayDataListItem[]
  canEdit: boolean
  onClose: () => void
  onSaved: () => void
}

/** Verbatim Access `btnSave_Click` guard messages. */
const BANK_REQUIRED_MSG = 'Please choose Bank!'
const BANK_ACCOUNT_REQUIRED_MSG = 'Please choose Bank Account!'

/** Paydata Information subform columns — 1:1 với datasheet Access
 *  `frmProcurementQryPayDataBank` (thứ tự cột + caption giữ nguyên). */
const PAYDATA_COLUMNS: {
  key: keyof PayDataListItem
  label: string
  align?: 'right'
  render?: (r: PayDataListItem) => string
}[] = [
  { key: 'paydata_p_id', label: 'Paydata ID', align: 'right' },
  { key: 'payment_date', label: 'Payment Date', render: (r) => formatDate(r.payment_date) },
  { key: 'contract_no', label: 'Contract No' },
  { key: 'xntt_no', label: 'XNTT No' },
  { key: 'truck_id', label: 'Truck ID' },
  { key: 'payment_type', label: 'Payment Type' },
  { key: 'bank', label: 'Request - Bank' },
  { key: 'bank_account', label: 'BankAcc Number' },
  { key: 'account_name', label: 'BankAcc Name' },
  { key: 'supplier_id', label: 'BankInfo - Supplier ID' },
  { key: 'bank_name', label: 'Bank Name' },
]

/**
 * Change Bank dialog — converts Access form `frmProcurementQryPayDataBankAdd`
 * (Procurement Pay Data → Change Bank). Access caption là `Procurement - Change
 * Bank`; title web dùng tên navigation hiện tại → `Inbound Logistics - Change Bank`.
 *
 * Opened for a set of ticked Pay Data rows that all share one supplier
 * (`cpCode`). Layout khớp Access: Supplier Code + Supplier (readonly) → Bank +
 * Bank Account (combo, account cascades from bank per `bank_AfterUpdate` →
 * `Load_Source`) → label "Paydata Information" + subform datasheet liệt kê các
 * dòng đang đổi. Save applies `{ bank, bank_account }` to each ticked record.
 *
 * Autocomplete combos inject the current value into options. No fullScreen;
 * mobile stacks to a single column with internal scroll.
 */
export default function PayDataChangeBankDialog({
  open,
  cpCode,
  supplierLabel,
  rows,
  canEdit,
  onClose,
  onSaved,
}: Props) {
  const snack = useSnackbar()
  const [updatePayData, { isLoading: isSaving }] = useUpdatePayDataMutation()

  const [fetchBanks, { data: bankData, isFetching: banksLoading, originalArgs: bankArgs }] =
    useLazyPayDataBankLookupQuery()
  const [
    fetchBankAccounts,
    { data: accountData, isFetching: accountsLoading, originalArgs: accountArgs },
  ] = useLazyPayDataBankAccountLookupQuery()

  const [bank, setBank] = useState<string>('')
  const [bankAccount, setBankAccount] = useState<string>('')
  const [bankError, setBankError] = useState<string>('')
  const [accountError, setAccountError] = useState<string>('')

  const paydataIds = useMemo(() => rows.map((r) => r.paydata_p_id), [rows])

  // Re-init + load bank options each time the dialog opens.
  useEffect(() => {
    if (!open) return
    setBank('')
    setBankAccount('')
    setBankError('')
    setAccountError('')
    if (cpCode) void fetchBanks({ cpCode })
  }, [open, cpCode, fetchBanks])

  // Cascade: reload bank-account options whenever the picked bank changes
  // (Access `bank_AfterUpdate` → `Load_Source`).
  useEffect(() => {
    if (!open || !cpCode || !bank) return
    void fetchBankAccounts({ cpCode, bank })
  }, [open, cpCode, bank, fetchBankAccounts])

  // Auto-select Bank when only one option loads for this supplier. Guard on
  // `bankArgs.cpCode` so we never act on stale results from a previous supplier.
  useEffect(() => {
    if (!open || banksLoading || bankArgs?.cpCode !== cpCode) return
    const items = bankData?.items ?? []
    if (!bank && items.length === 1) {
      setBank(items[0].value)
      setBankAccount('')
      setBankError('')
    }
  }, [open, banksLoading, bankArgs, bankData, bank, cpCode])

  // Auto-select Bank Account when only one option loads for the picked bank.
  // Guard on `accountArgs` so stale results (previous bank) can't be chosen.
  useEffect(() => {
    if (!open || accountsLoading) return
    if (accountArgs?.cpCode !== cpCode || accountArgs?.bank !== bank) return
    const items = accountData?.items ?? []
    if (bank && !bankAccount && items.length === 1) {
      setBankAccount(items[0].value)
      setAccountError('')
    }
  }, [open, accountsLoading, accountArgs, accountData, bank, bankAccount, cpCode])

  // Inject the current value into options so a stored value never shows blank.
  const bankOptions = useMemo<PayDataLookupItem[]>(() => {
    const items = bankData?.items ?? []
    if (bank && !items.some((it) => it.value === bank)) {
      return [{ value: bank, label: bank }, ...items]
    }
    return items
  }, [bankData, bank])

  const accountOptions = useMemo<PayDataLookupItem[]>(() => {
    const items = accountData?.items ?? []
    if (bankAccount && !items.some((it) => it.value === bankAccount)) {
      return [{ value: bankAccount, label: bankAccount }, ...items]
    }
    return items
  }, [accountData, bankAccount])

  const bankValue = useMemo(
    () => bankOptions.find((it) => it.value === bank) ?? null,
    [bankOptions, bank],
  )
  const accountValue = useMemo(
    () => accountOptions.find((it) => it.value === bankAccount) ?? null,
    [accountOptions, bankAccount],
  )

  const handleSave = async () => {
    if (!canEdit) return
    if (!bank || bank.trim() === '') {
      setBankError(BANK_REQUIRED_MSG)
      snack.warning(BANK_REQUIRED_MSG)
      return
    }
    if (!bankAccount || bankAccount.trim() === '') {
      setAccountError(BANK_ACCOUNT_REQUIRED_MSG)
      snack.warning(BANK_ACCOUNT_REQUIRED_MSG)
      return
    }
    try {
      for (const id of paydataIds) {
        await updatePayData({ id, body: { bank, bank_account: bankAccount } }).unwrap()
      }
      snack.success('Successfully Updated')
      onSaved()
      onClose()
    } catch (err) {
      snack.error(getErrorMessage(err, 'Failed to update bank for the selected pay data'))
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      scroll="paper"
      PaperProps={{ sx: { width: '96vw', maxWidth: 1600 } }}
    >
      <DialogTitle sx={dialogTitleSx}>
        <Typography
          component="span"
          noWrap
          title="Inbound Logistics - Change Bank"
          sx={dialogTitleTextSx}
        >
          Inbound Logistics - Change Bank
        </Typography>
        <Stack direction="row" spacing={0.5} sx={dialogTitleActionsSx}>
          <Button
            variant="contained"
            size="small"
            onClick={() => void handleSave()}
            disabled={isSaving || !canEdit}
            sx={dialogHeaderBtnSx}
          >
            {isSaving ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={onClose}
            disabled={isSaving}
            sx={dialogHeaderBtnSx}
          >
            Cancel
          </Button>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent dividers sx={compactDialogContentSx}>
        <Box
          sx={{
            display: 'grid',
            gap: 1.5,
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          }}
        >
          {/* Row 1: Supplier Code (readonly) | Supplier (readonly, Access DLookUp name) */}
          <TextField
            label="Supplier Code"
            size="small"
            fullWidth
            value={cpCode || ''}
            InputProps={{ readOnly: true }}
            InputLabelProps={{ shrink: true }}
            sx={roSx}
          />
          <TextField
            label="Supplier"
            size="small"
            fullWidth
            value={supplierLabel || ''}
            InputProps={{ readOnly: true }}
            InputLabelProps={{ shrink: true }}
            sx={roSx}
          />

          {/* Row 2: Bank | Bank Account (cascades from Bank) */}
          <Autocomplete<PayDataLookupItem>
            size="small"
            options={bankOptions}
            loading={banksLoading}
            value={bankValue}
            getOptionLabel={(o) => o.label ?? ''}
            isOptionEqualToValue={(o, v) => o.value === v.value}
            onChange={(_e, val) => {
              setBank(val?.value ?? '')
              // Cascade reset: bank changed → clear dependent account.
              setBankAccount('')
              if (val?.value) setBankError('')
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Bank *"
                error={!!bankError}
                helperText={bankError || undefined}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {banksLoading ? <CircularProgress size={14} color="inherit" /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <Autocomplete<PayDataLookupItem>
            size="small"
            options={accountOptions}
            loading={accountsLoading}
            disabled={!bank}
            value={accountValue}
            getOptionLabel={(o) => o.label ?? ''}
            isOptionEqualToValue={(o, v) => o.value === v.value}
            onChange={(_e, val) => {
              setBankAccount(val?.value ?? '')
              if (val?.value) setAccountError('')
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Bank Account *"
                error={!!accountError}
                helperText={accountError || undefined}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {accountsLoading ? (
                        <CircularProgress size={14} color="inherit" />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Box>

        {/* Paydata Information — Access label `Label831` + subform datasheet */}
        <Typography sx={{ ...sectionHeaderSx, mt: 2.5, mb: 1 }}>
          Paydata Information
        </Typography>
        <Box sx={{ overflowX: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
          <Table size="small" stickyHeader sx={{ minWidth: 900 }}>
            <TableHead>
              <TableRow>
                {PAYDATA_COLUMNS.map((c) => (
                  <TableCell
                    key={c.key}
                    align={c.align}
                    sx={{ whiteSpace: 'nowrap', fontWeight: 700, bgcolor: 'background.paper' }}
                  >
                    {c.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={PAYDATA_COLUMNS.length} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      No pay data selected.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((r) => (
                  <TableRow key={r.paydata_p_id} hover>
                    {PAYDATA_COLUMNS.map((c) => {
                      const text = c.render ? c.render(r) : (r[c.key] ?? '') as string | number
                      return (
                        <TableCell
                          key={c.key}
                          align={c.align}
                          sx={{
                            whiteSpace: 'nowrap',
                            fontVariantNumeric: c.align === 'right' ? 'tabular-nums' : undefined,
                          }}
                        >
                          {text === null || text === undefined ? '' : String(text)}
                        </TableCell>
                      )
                    })}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Box>
      </DialogContent>
    </Dialog>
  )
}
